import { useEffect, useMemo, useState } from 'react';
import { Toasts, useModalContext } from '@fourthwall/components';
import { useDnsQuery } from '@modules/Dashboard/hooks/useDnsQuery';
import { useIsMutating } from '@tanstack/react-query';
import { useEntri } from '../../../hooks/useEntri';
import { createDomainMutationKey, useCreateDomainMutation, } from '../../../queries/useCreateDomainMutation';
import { useDeleteDomainMutation } from '../../../queries/useDeleteDomainMutation';
import { usePaidDomain } from '../../ClaimDomain';
import { ConfirmDomainConnectModal } from '../../ConfirmDomainConnectModal/ConfirmDomainConnectModal';
const DNS_REFETCH_INTERVAL = 5_000;
export const useCustomDomain = () => {
    const { dnsQuery, invalidateDnsQuery } = useDnsQuery([], {
        refetchInterval: DNS_REFETCH_INTERVAL,
    });
    const { currentDomain, isLoading: isLoadingPaidDomain } = usePaidDomain();
    const { deleteDomainMutation } = useDeleteDomainMutation();
    const { createDomainMutation } = useCreateDomainMutation();
    const isCreatingDomain = useIsMutating([createDomainMutationKey]) > 0;
    const { open } = useModalContext();
    const [isConnecting, setIsConnecting] = useState(false);
    const dnsList = dnsQuery.data?.dnsList;
    const { isEntriLoaded, isAutomaticSetupInProgress, isGettingProviderDetailsInProgress, connectDomain, } = useEntri({
        enabled: !!dnsList?.retryAllowed,
    });
    const configurationStatus = useMemo(() => {
        if (dnsQuery.isLoading || isLoadingPaidDomain) {
            return 'LOADING';
        }
        if (!dnsList && !currentDomain) {
            return 'NONE';
        }
        if (!currentDomain && dnsList) {
            return 'CUSTOM_DOMAIN_ONLY';
        }
        if ((currentDomain && !dnsList) ||
            (currentDomain && dnsList && currentDomain.fullDomainWithTld === dnsList.domain)) {
            return 'PAID_DOMAIN_ONLY';
        }
        if (currentDomain && dnsList && currentDomain.fullDomainWithTld !== dnsList.domain) {
            return 'BOTH';
        }
    }, [dnsQuery.isLoading, isLoadingPaidDomain, dnsList, currentDomain]);
    const domainName = useMemo(() => {
        if (configurationStatus === 'PAID_DOMAIN_ONLY') {
            return currentDomain?.fullDomainWithTld || '';
        }
        return dnsList?.domain || '';
    }, [currentDomain, configurationStatus, dnsList]);
    const paidDomainName = currentDomain?.fullDomainWithTld || '';
    const deleteCustomDomain = () => {
        deleteDomainMutation.mutate(undefined, {
            onSuccess: () => {
                invalidateDnsQuery();
            },
        });
    };
    const connectPaidDomainConfirm = () => {
        createDomainMutation.mutate({
            domain: paidDomainName,
        }, {
            onSuccess: () => {
                invalidateDnsQuery();
                Toasts.notify("We're pulling information about the domain. This can take up to 30 seconds.", {
                    type: 'info',
                    autoClose: false,
                });
            },
        });
    };
    const connectPaidDomain = () => {
        if (!dnsList || dnsList.domain === paidDomainName) {
            connectPaidDomainConfirm();
            return;
        }
        open(ConfirmDomainConnectModal, {
            newDomainName: paidDomainName,
            onConfirm: connectPaidDomainConfirm,
        });
    };
    const retryCustomDomainConnection = useMemo(() => {
        if (!dnsList || !dnsList.retryAllowed || !isEntriLoaded)
            return;
        return () => {
            connectDomain(dnsList);
        };
    }, [dnsList, isEntriLoaded, connectDomain]);
    useEffect(() => {
        if (dnsList?.status && dnsList.status !== 'NOT_CONNECTED') {
            setIsConnecting(false);
        }
    }, [dnsList?.status]);
    useEffect(() => {
        if (isCreatingDomain) {
            setIsConnecting(true);
        }
    }, [isCreatingDomain]);
    return {
        configurationStatus,
        connectionStatus: dnsList?.status || 'NOT_CONNECTED',
        domainName,
        paidDomainName,
        isDnsConfigured: !!dnsList,
        isConnecting: createDomainMutation.isLoading || isConnecting,
        isDeleting: deleteDomainMutation.isLoading,
        isRetrying: isGettingProviderDetailsInProgress || isAutomaticSetupInProgress,
        deleteCustomDomain,
        connectPaidDomain,
        retryCustomDomainConnection,
    };
};
